import { type UIEventHandler, useCallback, useEffect, useRef } from "react";

export const DEFAULT_INFINITE_SCROLL_THRESHOLD_PX = 48;

export const getScrollDistanceFromBottom = (element: HTMLElement): number =>
  element.scrollHeight - element.scrollTop - element.clientHeight;

export const useInfiniteScroll = <T extends HTMLElement>(
  isLoading: boolean,
  onLoadMore: () => void
): ((node: T | null) => void) => {
  const observer = useRef<IntersectionObserver | undefined>(undefined);

  const cleanup = (): void => {
    if (observer.current) {
      observer.current.disconnect();
      observer.current = undefined;
    }
  };

  useEffect(() => cleanup, []);

  return useCallback(
    (node: T | null) => {
      if (isLoading) {
        return;
      }
      cleanup();

      observer.current = new IntersectionObserver((entries) => {
        // this hook only supports monitoring one element
        if (entries[0]?.isIntersecting) {
          onLoadMore();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [isLoading, onLoadMore]
  );
};

export const useInfiniteScrollOnScroll = <T extends HTMLElement>(
  onLoadMore: () => void,
  thresholdPx = DEFAULT_INFINITE_SCROLL_THRESHOLD_PX
): UIEventHandler<T> =>
  useCallback(
    (event) => {
      if (getScrollDistanceFromBottom(event.currentTarget) <= thresholdPx) {
        onLoadMore();
      }
    },
    [onLoadMore, thresholdPx]
  );
